import React, { useMemo } from "react";
import styled from "styled-components";
import { ResponsiveBar } from "@nivo/bar";
import { Empty } from "antd";
import { EngagementType } from "../../models/stewardshipModels";
import {
  ThemeDynamicsBarDatum,
  ThemeDynamicsSegmentKey,
} from "../../types/linkageDashboardTypes";
import {
  Card,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { ElementContainer } from "../../components/containers/styledComponents";
import { LegendCircle } from "../../components/visuals/styledComponents";
import {
  greyText,
  compassLightGreen,
  darkGreen,
  compassBordeaux,
  compassGreyBackground,
} from "../../styles";
import { formatReadable } from "../../utils/formattingUtils";
import environmental from "../../assets/svg/environmental_circle.svg";
import social from "../../assets/svg/social_circle.svg";
import governance from "../../assets/svg/governance_circle.svg";
import strategy from "../../assets/svg/strategy_circle.svg";

// =============================================================================
// CONSTANTS
// =============================================================================

const SEGMENT_KEYS: ThemeDynamicsSegmentKey[] = [
  "positive_alignment__same_manager",
  "positive_alignment__cross_manager",
  "negative_tension__same_manager",
  "negative_tension__cross_manager",
  "other__same_manager",
  "other__cross_manager",
];

const SEGMENT_COLORS: Record<ThemeDynamicsSegmentKey, string> = {
  positive_alignment__same_manager: compassLightGreen,
  positive_alignment__cross_manager: "#80F0C0",
  negative_tension__same_manager: "#FD7D96",
  negative_tension__cross_manager: compassBordeaux,
  other__same_manager: compassGreyBackground,
  other__cross_manager: "#8B909A",
};

const SEGMENT_LABELS: Record<ThemeDynamicsSegmentKey, string> = {
  positive_alignment__same_manager: "Positive – Same Manager",
  positive_alignment__cross_manager: "Positive – Cross Manager",
  negative_tension__same_manager: "Tension – Same Manager",
  negative_tension__cross_manager: "Tension – Cross Manager",
  other__same_manager: "Other – Same Manager",
  other__cross_manager: "Other – Cross Manager",
};

const META_THEME_ICONS: Record<EngagementType, string> = {
  [EngagementType.Environmental]: environmental,
  [EngagementType.Social]: social,
  [EngagementType.Governance]: governance,
  [EngagementType.StrategyRiskCommunication]: strategy,
};

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ChartContainer = styled.div<{ barCount: number }>`
  height: ${({ barCount }): number => Math.max(barCount * 28 + 80, 200)}px;
  width: 90%;
  margin-bottom: 16px;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 8px;
  padding-top: 8px;
  flex-shrink: 0;
`;

// =============================================================================
// COMPONENT
// =============================================================================

interface ThemeDynamicsChartProps {
  data: ThemeDynamicsBarDatum[];
  isLoading: boolean;
}

export const ThemeDynamicsChart: React.FC<ThemeDynamicsChartProps> = ({
  data,
  isLoading,
}) => {
  const isEmpty = !data || data.length === 0;

  // Build a lookup from theme name → meta_theme for the custom tick
  const themeMetaMap = useMemo(() => {
    const map: Record<string, EngagementType> = {};
    for (const d of data) {
      map[d.theme] =
        (d.meta_theme as EngagementType) ||
        EngagementType.StrategyRiskCommunication;
    }
    return map;
  }, [data]);

  // Custom tick component for left axis that shows meta theme icon
  const CustomLeftTick = (tick: {
    x: number;
    y: number;
    value: string;
  }): JSX.Element => {
    const metaTheme =
      themeMetaMap[tick.value] || EngagementType.StrategyRiskCommunication;
    const icon = META_THEME_ICONS[metaTheme];
    return (
      <g transform={`translate(${tick.x},${tick.y})`}>
        <image href={icon} x={-30} y={-8} width={16} height={16} />
        <text
          x={-34}
          y={0}
          textAnchor="end"
          dominantBaseline="central"
          style={{
            fontFamily: "Apercu",
            fontSize: 11,
            fill: greyText,
          }}
        >
          {formatReadable(tick.value)}
        </text>
      </g>
    );
  };

  return (
    <Card h="auto" m="0 20px" isLoading={isLoading}>
      <RowContainer mt={0} mb={8}>
        <CompassParagraph fontSize={16} bold={true}>
          Theme Dynamics
        </CompassParagraph>
      </RowContainer>
      <CompassParagraph fontSize={12} color={greyText} mb={20}>
        Alignment vs. tension breakdown by engagement theme and manager
        relationship
      </CompassParagraph>

      {isEmpty && !isLoading ? (
        <ElementContainer
          ai="center"
          jc="center"
          h="200px"
          w="100%"
          flexDirection="column"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No theme dynamics data available for the selected filters."
          />
        </ElementContainer>
      ) : (
        <>
          <ChartContainer barCount={data.length}>
            <ResponsiveBar
              data={data}
              keys={SEGMENT_KEYS}
              indexBy="theme"
              layout="horizontal"
              margin={{ top: 6, right: 20, bottom: 50, left: 240 }}
              padding={0.45}
              borderRadius={3}
              valueScale={{ type: "linear" }}
              indexScale={{ type: "band", round: true }}
              colors={(bar): string =>
                SEGMENT_COLORS[bar.id as ThemeDynamicsSegmentKey] || "#ccc"
              }
              enableGridX={true}
              enableGridY={false}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: (v): string => `${Math.round(Number(v) * 100)}%`,
                legend: "Proportion within theme",
                legendPosition: "middle",
                legendOffset: 40,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                tickRotation: 0,
                renderTick: CustomLeftTick,
              }}
              enableLabel={false}
              isInteractive={true}
              tooltip={({ id, value, indexValue }): JSX.Element => (
                <div
                  style={{
                    background: "white",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    fontFamily: "Apercu",
                    fontSize: 12,
                  }}
                >
                  <strong>{formatReadable(indexValue as string)}</strong>
                  <br />
                  {SEGMENT_LABELS[id as ThemeDynamicsSegmentKey]}:{" "}
                  {(Number(value) * 100).toFixed(1)}%
                </div>
              )}
              theme={{
                text: { fontFamily: "Apercu", fontSize: 11, fill: darkGreen },
                axis: {
                  ticks: {
                    text: {
                      fontFamily: "Apercu",
                      fontSize: 11,
                      fill: greyText,
                    },
                  },
                  legend: {
                    text: {
                      fontFamily: "Apercu",
                      fontSize: 12,
                      fill: darkGreen,
                    },
                  },
                },
                grid: { line: { stroke: "#e2e8f0", strokeDasharray: "4 4" } },
              }}
              animate={true}
              motionConfig="gentle"
            />
          </ChartContainer>

          <LegendWrapper>
            {SEGMENT_KEYS.map((key) => (
              <ElementContainer key={key} ai="center" mr={4}>
                <LegendCircle color={SEGMENT_COLORS[key]} />
                <CompassParagraph fontSize={11} ml={6} color={greyText}>
                  {SEGMENT_LABELS[key]}
                </CompassParagraph>
              </ElementContainer>
            ))}
          </LegendWrapper>
        </>
      )}
    </Card>
  );
};
